import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    MessageSquare,
    Plus,
    Clock,
    ChevronRight,
    Camera,
    Image,
    Send,
    CheckCircle,
    Loader2,
    ArrowLeft,
    Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface SupportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface Ticket {
    id: string;
    ticket_number: string;
    subject: string;
    status: string;
    priority: string;
    category: string;
    created_at: string;
}

const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<'past' | 'new'>('past');
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // New ticket form state
    const [formData, setFormData] = useState({
        category: 'soru',
        priority: 'orta',
        subject: '',
        description: ''
    });
    const [attachments, setAttachments] = useState<File[]>([]);

    // Ticket Detail State
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
    const [messages, setMessages] = useState<any[]>([]);
    const [isFetchingMessages, setIsFetchingMessages] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [isSendingReply, setIsSendingReply] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (selectedTicket) {
            fetchMessages(selectedTicket.id);
        }
    }, [selectedTicket]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchMessages = async (ticketId: string) => {
        setIsFetchingMessages(true);
        try {
            const { data, error } = await supabase
                .from('ticket_messages')
                .select(`
                    *,
                    sender:profiles!ticket_messages_sender_id_fkey(name, role, unit_type)
                `)
                .eq('ticket_id', ticketId)
                .eq('is_internal', false)
                .order('created_at', { ascending: true });

            if (error) throw error;
            setMessages(data || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setIsFetchingMessages(false);
        }
    };

    const handleSendReply = async () => {
        if (!user || !selectedTicket || !replyMessage.trim()) return;

        setIsSendingReply(true);
        try {
            const { error } = await supabase
                .from('ticket_messages')
                .insert({
                    ticket_id: selectedTicket.id,
                    sender_id: user.id,
                    message: replyMessage,
                    is_internal: false
                });

            if (error) throw error;
            setReplyMessage('');
            fetchMessages(selectedTicket.id);
        } catch (error) {
            console.error('Error sending reply:', error);
            alert('Yanıt gönderilemedi');
        } finally {
            setIsSendingReply(false);
        }
    };

    useEffect(() => {
        if (isOpen && user) {
            fetchTickets();
        }
    }, [isOpen, user]);

    const fetchTickets = async () => {
        if (!user) return;
        setIsLoading(true);
        try {
            const ALLOWED_VIEWER_IDS = [
                'f776a720-9606-459c-a712-aaea95804f30', // Kaya Bayezit
                'c127acd1-8cf5-4f3b-9a09-c1e25c7c4366'  // Medine Güzel
            ];

            let query = supabase
                .from('support_tickets')
                .select(`
                    id, 
                    ticket_number, 
                    subject, 
                    description, 
                    status, 
                    priority, 
                    category, 
                    attachments, 
                    created_at,
                    profiles:user_id (name) 
                `)
                .order('created_at', { ascending: false });

            // Sadece normal kullanıcılar için filtrele
            // Admin, Teknik Destek ve Özel Kullanıcılar TÜMÜNÜ görür
            const canViewAll =
                user.role === 'admin' ||
                user.unitType === 'teknik_destek' ||
                ALLOWED_VIEWER_IDS.includes(user.id);

            if (!canViewAll) {
                query = query.eq('user_id', user.id);
            }

            const { data, error } = await query;

            if (error) throw error;
            setTickets(data || []);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !formData.subject.trim() || !formData.description.trim()) return;

        setIsSubmitting(true);
        try {
            // Upload attachments if any
            const uploadedUrls: string[] = [];
            for (const file of attachments) {
                const fileName = `support/${user.id}/${Date.now()}-${file.name}`;
                const { error: uploadError } = await supabase.storage
                    .from('documents')
                    .upload(fileName, file);

                if (!uploadError) {
                    const { data: urlData } = supabase.storage
                        .from('documents')
                        .getPublicUrl(fileName);
                    uploadedUrls.push(urlData.publicUrl);
                }
            }

            const { error } = await supabase
                .from('support_tickets')
                .insert({
                    user_id: user.id,
                    branch_id: user.branchId || null,
                    category: formData.category,
                    priority: formData.priority,
                    subject: formData.subject,
                    description: formData.description,
                    attachments: uploadedUrls
                });

            if (error) throw error;

            setSubmitSuccess(true);

            // Send SMS Notification
            try {
                const smsMessage = `[Destek Talebi]\nGönderen: ${user.name}\nKategori: ${formData.category}\nÖncelik: ${formData.priority}\nKonu: ${formData.subject}\nAçıklama: ${formData.description}`;

                await supabase.functions.invoke('send-sms', {
                    body: {
                        phones: ['05423297878'],
                        message: smsMessage,
                        userId: user.id
                    }
                });
                console.log('Support ticket SMS sent successfully');
            } catch (smsError) {
                console.error('Error sending support ticket SMS:', smsError);
                // Don't block UI success state even if SMS fails
            }

            setFormData({ category: 'soru', priority: 'orta', subject: '', description: '' });
            setAttachments([]);
            fetchTickets();

            setTimeout(() => {
                setSubmitSuccess(false);
                setActiveTab('past');
            }, 2000);
        } catch (error: any) {
            console.error('Error creating ticket:', error);
            alert(`Talep oluşturulurken hata oluştu: ${error.message || 'Bilinmeyen Hata'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && attachments.length + files.length <= 5) {
            setAttachments(prev => [...prev, ...Array.from(files)]);
        }
    };

    const handleScreenshot = async () => {
        try {
            // Use html2canvas or similar if available
            alert('Ekran görüntüsü özelliği yakında eklenecek');
        } catch (error) {
            console.error('Screenshot error:', error);
        }
    };

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            acik: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
            islemde: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
            beklemede: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
            cozuldu: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            kapatildi: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
        };
        const labels: Record<string, string> = {
            acik: 'Açık',
            islemde: 'İşlemde',
            beklemede: 'Beklemede',
            cozuldu: 'Çözüldü',
            kapatildi: 'Kapatıldı'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.acik}`}>
                {labels[status] || status}
            </span>
        );
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        Destek Hattı
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('past')}
                        className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'past'
                            ? 'text-purple-600 border-b-2 border-purple-600 dark:text-purple-400'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                            }`}
                    >
                        <Clock className="w-4 h-4" />
                        Geçmiş Talepler
                    </button>
                    <button
                        onClick={() => setActiveTab('new')}
                        className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'new'
                            ? 'text-purple-600 border-b-2 border-purple-600 dark:text-purple-400'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
                            }`}
                    >
                        <Plus className="w-4 h-4" />
                        Yeni Talep
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {activeTab === 'past' ? (
                        <div className="space-y-3">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                                </div>
                            ) : selectedTicket ? (
                                /* Ticket Detail View */
                                <div className="space-y-6">
                                    <button
                                        onClick={() => setSelectedTicket(null)}
                                        className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium mb-4"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        Geri
                                    </button>

                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-750">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs text-purple-600 font-bold">{selectedTicket.ticket_number}</span>
                                            {getStatusBadge(selectedTicket.status)}
                                        </div>
                                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">{selectedTicket.subject}</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{(selectedTicket as any).description || 'Açıklama yok'}</p>

                                        {(selectedTicket as any).attachments?.length > 0 && (
                                            <div className="flex gap-2 mb-3">
                                                {(selectedTicket as any).attachments.map((url: string, i: number) => (
                                                    <div key={i} className="w-16 h-16 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                                        <img src={url} alt="attachment" className="w-full h-full object-cover" />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <span className="text-[10px] text-gray-400">{formatDate(selectedTicket.created_at)}</span>
                                    </div>

                                    {/* Messages */}
                                    <div className="space-y-4">
                                        {isFetchingMessages ? (
                                            <div className="flex justify-center py-4">
                                                <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                                            </div>
                                        ) : (
                                            <>
                                                {messages.map((msg) => (
                                                    <div key={msg.id} className={`p-4 rounded-xl border ${msg.sender_id === user?.id ? 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800' : 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/40'}`}>
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-bold text-gray-900 dark:text-white">{msg.sender?.name}</span>
                                                                {msg.sender?.unit_type === 'teknik_destek' && (
                                                                    <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-bold">Teknik Destek</span>
                                                                )}
                                                            </div>
                                                            <span className="text-[10px] text-gray-400">{new Date(msg.created_at).toLocaleString('tr-TR')}</span>
                                                        </div>
                                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.message}</p>
                                                    </div>
                                                ))}
                                                <div ref={messagesEndRef} />
                                            </>
                                        )}
                                    </div>

                                    {/* Reply Input */}
                                    <div className="sticky bottom-0 bg-white dark:bg-gray-900 pt-4">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Yanıtınızı yazın..."
                                                value={replyMessage}
                                                onChange={(e) => setReplyMessage(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSendReply()}
                                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none pr-12"
                                            />
                                            <button
                                                onClick={handleSendReply}
                                                disabled={isSendingReply || !replyMessage.trim()}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-purple-600 hover:text-purple-700 disabled:opacity-50 transition-colors"
                                            >
                                                {isSendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : tickets.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>Henüz destek talebiniz bulunmuyor</p>
                                </div>
                            ) : (
                                tickets.map(ticket => (
                                    <div
                                        key={ticket.id}
                                        onClick={() => setSelectedTicket(ticket)}
                                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-l-4 border-purple-500 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors cursor-pointer"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {ticket.ticket_number}
                                                </span>
                                                {(ticket as any).profiles?.name && (
                                                    <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 px-1.5 py-0.5 rounded font-medium">
                                                        {(ticket as any).profiles.name}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="font-medium text-gray-900 dark:text-white truncate">
                                                {ticket.subject}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {formatDate(ticket.created_at)}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3 ml-4">
                                            {getStatusBadge(ticket.status)}

                                            {/* Delete Button for authorized users */}
                                            {user && (user.role === 'admin' || user.unitType === 'teknik_destek' || ['f776a720-9606-459c-a712-aaea95804f30', 'c127acd1-8cf5-4f3b-9a09-c1e25c7c4366'].includes((user as any).id)) && (
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (await window.appConfirm('Bu talebi silmek istediğinizden emin misiniz?')) {
                                                            const handleDelete = async () => {
                                                                try {
                                                                    const { error } = await supabase
                                                                        .from('support_tickets')
                                                                        .delete()
                                                                        .eq('id', ticket.id);

                                                                    if (error) throw error;

                                                                    // Updates the list locally
                                                                    setTickets(prev => prev.filter(t => t.id !== ticket.id));
                                                                    if (selectedTicket?.id === ticket.id) setSelectedTicket(null);
                                                                } catch (err) {
                                                                    console.error('Error deleting ticket:', err);
                                                                    alert('Talep silinirken hata oluştu');
                                                                }
                                                            };
                                                            handleDelete();
                                                        }
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                                    title="Talebi Sil"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}

                                            <ChevronRight className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {submitSuccess ? (
                                <div className="text-center py-8">
                                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        Talep Oluşturuldu!
                                    </h3>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        En kısa sürede size dönüş yapacağız.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Category */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Kategori
                                        </label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                                        >
                                            <option value="soru">Soru</option>
                                            <option value="hata_bildirimi">Hata Bildirimi</option>
                                            <option value="ozellik_talebi">Özellik Talebi</option>
                                            <option value="diger">Diğer</option>
                                        </select>
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Öncelik
                                        </label>
                                        <div className="flex gap-2">
                                            {['dusuk', 'orta', 'yuksek', 'acil'].map(priority => (
                                                <button
                                                    key={priority}
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({ ...prev, priority }))}
                                                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${formData.priority === priority
                                                        ? 'bg-purple-100 border-purple-500 text-purple-700 dark:bg-purple-900/30 dark:border-purple-500 dark:text-purple-400'
                                                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400'
                                                        }`}
                                                >
                                                    {priority === 'dusuk' ? 'Düşük' : priority === 'orta' ? 'Orta' : priority === 'yuksek' ? 'Yüksek' : 'Acil'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Subject */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Konu <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.subject}
                                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                                            placeholder="Kısa bir başlık yazın"
                                            required
                                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Açıklama <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Sorununuzu veya talebinizi detaylı açıklayın..."
                                            required
                                            rows={4}
                                            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                                        />
                                    </div>

                                    {/* Attachments */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Ekran Görüntüleri/Görseller (Opsiyonel, Max: 5)
                                        </label>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={handleScreenshot}
                                                className="flex-1 py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center gap-1 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                                            >
                                                <Camera className="w-5 h-5" />
                                                <span className="text-xs">Screenshot</span>
                                            </button>
                                            <label className="flex-1 py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center gap-1 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                                                <Image className="w-5 h-5" />
                                                <span className="text-xs">Görsel Ekle</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handleFileChange}
                                                    className="hidden"
                                                />
                                            </label>
                                        </div>
                                        {attachments.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {attachments.map((file, index) => (
                                                    <span key={index} className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                                        {file.name}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !formData.subject.trim() || !formData.description.trim()}
                                        className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:from-purple-700 hover:to-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <>
                                                <Send className="w-5 h-5" />
                                                Talep Oluştur
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportModal;
